import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useMediaQuery,
  useTheme,
  Alert,
  Snackbar,
  Stack,
} from "@mui/material";
import { useDeliveryMethods } from "../../../hooks/useDeliveryMethods";
import DeliveryMethodTable from "../../../components/DeliveryMethodTable";
import AddDeliveryMethodForm from "../../../components/AddDeliveryMethodForm";
import { usePageContext } from "../../../context/PageContext";
import { useTranslation } from "react-i18next";

const DeliveryMethods = () => {
  const { t } = useTranslation("settings");
  const { currentPage, setCurrentPage } = usePageContext();
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    data,
    error,
    refetch,
    updateMutation,
    updateStatusMutation,
    deleteMutation,
    isLoading,
    isFetching,
    isRefetching,
  } = useDeliveryMethods(currentPage, pageSize, search);

  useEffect(() => {
    if (error) setErrorMessage("Erro ao carregar formas de entrega. Por favor, tente novamente.");
    else setErrorMessage(null);
  }, [error]);

  // debounce da busca
  useEffect(() => {
    const t = setTimeout(() => setSearch(String(searchInput ?? "").trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const handleEditDeliveryMethod = (row) => {
    setSelectedDeliveryMethod({ id: row.id });
    setErrorMessage(null);
  };

  const handleDeleteDeliveryMethod = (id) => {
    setConfirmDelete(id);
    setErrorMessage(null);
  };

  const confirmDeleteDeliveryMethod = () => {
    if (!confirmDelete) return;
    setIsProcessing(true);
    deleteMutation.mutate(confirmDelete, {
      onSuccess: () => {
        setSnack({ open: true, message: "Forma de entrega excluída com sucesso.", severity: "success" });
        setConfirmDelete(null);
        refetch();
      },
      onError: (err) => {
        setErrorMessage(
          err?.response?.data?.message || "Erro ao excluir a forma de entrega. Tente novamente."
        );
        setSnack({ open: true, message: "Falha ao excluir.", severity: "error" });
      },
      onSettled: () => setIsProcessing(false),
    });
  };

  const handleToggleStatus = (row) => {
    const newStatus = row.status === 1 ? 0 : 1;
    updateStatusMutation.mutate(
      { id: row.id, status: newStatus },
      {
        onSuccess: () => {
          setSnack({
            open: true,
            message: `Forma de entrega ${newStatus === 1 ? "ativada" : "inativada"} com sucesso.`,
            severity: "success",
          });
        },
        onError: () =>
          setSnack({ open: true, message: "Falha ao atualizar status.", severity: "error" }),
      }
    );
  };

  const handleDeleteSelected = async (ids) => {
    if (!ids?.length) return;
    setIsProcessing(true);
    setErrorMessage(null);
    const results = await Promise.allSettled(
      ids.map((id) => deleteMutation.mutateAsync(id))
    );
    const failed = results.filter((r) => r.status === "rejected");
    if (failed.length > 0) {
      setErrorMessage(
        failed.length === ids.length
          ? "Erro ao excluir as formas de entrega."
          : `${failed.length} de ${ids.length} exclusão(ões) falharam.`
      );
      setSnack({ open: true, message: "Falha em algumas exclusões.", severity: "error" });
    } else {
      setSnack({ open: true, message: "Formas de entrega excluídas com sucesso.", severity: "success" });
    }
    refetch();
    setIsProcessing(false);
  };

  const handlePageChange = (newPage) => setCurrentPage(newPage + 1);
  const handlePageSizeChange = (newPageSize) => setPageSize(newPageSize);

  const handleCloseForm = () => {
    setSelectedDeliveryMethod(null);
    setErrorMessage(null);
  };

  const busy = isLoading || isFetching || isRefetching || isProcessing;
  const pagination = data?.pagination || { total: 0, page: 1, totalPages: 1 };

  const headerRight = useMemo(
    () => (
      <Stack direction={isMobile ? "column" : "row"} spacing={1} alignItems="center">
        <Button
          variant="contained"
          color="primary"
          fullWidth={isMobile}
          onClick={() => setSelectedDeliveryMethod({})}
        >
          {t("deliveryMethodsPage.add")}
        </Button>
      </Stack>
    ),
    [isMobile]
  );

  return (
    <Box sx={{ pt: 1, overflowX: "hidden" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          mb: 2,
          gap: isMobile ? 2 : 0,
          px: 0,
        }}
      >
        <Typography variant="h4" gutterBottom>
          {t("deliveryMethodsPage.title")}
        </Typography>
        {headerRight}
      </Box>

      {errorMessage && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      )}

      {selectedDeliveryMethod ? (
        <AddDeliveryMethodForm
          deliveryMethod={selectedDeliveryMethod}
          isFetching={busy}
          onClose={handleCloseForm}
          onSubmit={(payload) => {
            // Retorna Promise para o form saber quando salvou (e.g. resetar "dirty")
            return new Promise((resolve, reject) => {
              setIsProcessing(true);
              updateMutation.mutate(payload, {
                onSuccess: (resp) => {
                  setSnack({ open: true, message: "Forma de entrega salva com sucesso.", severity: "success" });
                  refetch();

                  // Mantém no form (não fecha) e garante que "novo" vire "edição"
                  const savedId =
                    resp?.data?.id ??
                    resp?.data?.data?.id ??
                    payload?.id ??
                    null;
                  if (savedId) setSelectedDeliveryMethod({ id: savedId });

                  resolve(resp);
                },
                onError: (err) => {
                  setErrorMessage(err?.response?.data?.message || "Erro ao salvar a forma de entrega.");
                  setSnack({ open: true, message: "Falha ao salvar.", severity: "error" });
                  reject(err);
                },
                onSettled: () => setIsProcessing(false),
              });
            });
          }}
        />
      ) : (
        <Box sx={{ width: "95%", mx: "auto" }}>
          <DeliveryMethodTable
            deliveryMethods={data?.deliveryMethods || []}
            rowCount={pagination.total || 0}
            pageSize={pageSize}
            page={currentPage - 1}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            onSearch={(term) => setSearchInput(term == null ? "" : String(term))}
            isLoading={busy}
            onEditMethod={handleEditDeliveryMethod}
            onDeleteMethod={handleDeleteDeliveryMethod}
            onDeleteSelected={handleDeleteSelected}
            onToggleStatus={handleToggleStatus}
          />
        </Box>
      )}

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>{t("shared.deleteConfirmation")}</DialogTitle>
        <DialogContent>
          <Typography>
            {isProcessing ? t("shared.processingDelete") : t("deliveryMethodsPage.deleteConfirm")}
          </Typography>
        </DialogContent>
        <DialogActions>
          {!isProcessing && (
            <>
              <Button onClick={() => setConfirmDelete(null)}>{t("shared.cancel")}</Button>
              <Button color="error" onClick={confirmDeleteDeliveryMethod}>
                {t("shared.delete")}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        message={snack.message}
      />
    </Box>
  );
};

export default DeliveryMethods;
